/**
 * Header - Full Screen Block JavaScript
 * LCP Optimized: Deferred animations + lazy video loading
 */

(function() {
    'use strict';

    // Initialize block functionality
    function initHeaderFullScreen() {
        const blocks = document.querySelectorAll('.header-full-screen');

        blocks.forEach(block => {

            // Add GSAP animations after content is visible
            initDeferredAnimations(block);

            // Initialize smooth scroll for scroll indicator
            initSmoothScroll(block);

            // Initialize gallery carousel if present
            initGalleryCarousel(block);

            console.log('[BLOCK] header-full-screen block initialized (LCP optimized)');
        });
    }

    /**
     * Initialize hero gallery carousel (autoplay)
     */
    function initGalleryCarousel(block) {
        const carousel = block.querySelector('.hero-gallery-carousel');
        if (!carousel) return;

        const track = carousel.querySelector('.hero-gallery-carousel__track');
        const slides = carousel.querySelectorAll('.hero-gallery-carousel__slide');

        if (!track || slides.length < 2) return;

        const slideCount = slides.length;
        let currentIndex = 0;
        let autoAdvanceTimer = null;
        const AUTO_ADVANCE_MS = 4000;

        function getScrollAmount() {
            return track.offsetWidth || track.clientWidth;
        }

        function goToSlide(index) {
            currentIndex = Math.max(0, Math.min(index, slideCount - 1));
            const slideWidth = getScrollAmount();
            if (slideWidth > 0) {
                track.scrollTo({
                    left: currentIndex * slideWidth,
                    behavior: 'smooth'
                });
            }
            resetAutoAdvance();
        }

        function resetAutoAdvance() {
            if (autoAdvanceTimer) clearInterval(autoAdvanceTimer);
            autoAdvanceTimer = setInterval(() => {
                const nextIndex = currentIndex >= slideCount - 1 ? 0 : currentIndex + 1;
                goToSlide(nextIndex);
            }, AUTO_ADVANCE_MS);
        }

        track.addEventListener('scroll', () => {
            const slideWidth = getScrollAmount();
            if (slideWidth > 0) {
                currentIndex = Math.round(track.scrollLeft / slideWidth);
                resetAutoAdvance();
            }
        }, { passive: true });

        // Start autoplay after layout is ready (handles offsetWidth being 0 on initial load)
        function startAutoplay() {
            if (getScrollAmount() > 0) {
                resetAutoAdvance();
            } else {
                requestAnimationFrame(startAutoplay);
            }
        }
        startAutoplay();
    }

    /**
     * Smooth scroll to next section when scroll indicator is clicked
     */
    function initSmoothScroll(block) {
        const scrollIndicator = block.querySelector('.scroll-indicator');
        if (!scrollIndicator) return;

        scrollIndicator.addEventListener('click', function(e) {
            e.preventDefault();

            // Find the next section after this block
            const nextElement = block.nextElementSibling;

            if (nextElement) {
                nextElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            } else {
                // If no next element, scroll down one viewport
                window.scrollBy({
                    top: window.innerHeight,
                    behavior: 'smooth'
                });
            }

            console.log('[BLOCK] Smooth scroll triggered');
        });
    }

    /**
     * Add GSAP animations after LCP to not block rendering
     */
    function initDeferredAnimations(block) {
        // Function to run animations
        function runAnimations() {
            // Get animation elements
            const hallmarkWrapper = block.querySelector('.hallmark-wrapper');
            const hallmarkLogo = block.querySelector('.hallmark-logo');

            // Set initial states
            if (hallmarkWrapper) {
                gsap.set(hallmarkWrapper, { opacity: 0, force3D: false });
            }

            // Create GSAP timeline
            const tl = gsap.timeline({
                defaults: { ease: 'power3.out' }
            });

            // 1. Hallmark SVG drawing animation using DrawSVGPlugin
            if (hallmarkLogo && hallmarkWrapper) {
                const isSVG = hallmarkLogo.tagName === 'svg' || hallmarkLogo.querySelector('svg');
                console.log('[BLOCK] Hallmark found:', hallmarkLogo);
                console.log('[BLOCK] Is SVG:', isSVG);

                if (isSVG) {
                    const svg = hallmarkLogo.tagName === 'svg' ? hallmarkLogo : hallmarkLogo.querySelector('svg');
                    const paths = svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse');
                    console.log('[BLOCK] SVG paths found:', paths.length);
                    console.log('[BLOCK] DrawSVGPlugin available:', typeof window.DrawSVGPlugin);

                    // Fade in wrapper
                    tl.to(hallmarkWrapper, {
                        opacity: 1,
                        force3D: false,
                        duration: 0.3
                    }, 0);

                    // Draw SVG paths using DrawSVGPlugin
                    if (paths.length > 0) {
                        // Check if DrawSVGPlugin is actually registered
                        if (typeof window.DrawSVGPlugin !== 'undefined') {
                            console.log('[BLOCK] Using DrawSVGPlugin');

                            // Set initial states BEFORE animation to prevent flash
                            gsap.set(paths, {
                                drawSVG: '0%',
                                fillOpacity: 0
                            });

                            // Draw the stroke to 100%, then fade in fill
                            tl.to(paths, {
                                drawSVG: '100%',
                                duration: 1.5,
                                ease: 'power2.inOut',
                                stagger: 0.1
                            }, 0.2)
                            // Then fade in the fill (starts when stroke is almost done)
                            .to(paths, {
                                fillOpacity: 1,
                                duration: 0.6,
                                ease: 'power2.out'
                            }, '-=0.3');
                        } else {
                            console.warn('[BLOCK] DrawSVGPlugin not available, using fallback strokeDashoffset animation');
                            // Fallback to free stroke animation
                            paths.forEach(path => {
                                const length = path.getTotalLength ? path.getTotalLength() : 0;
                                if (length) {
                                    path.style.strokeDasharray = length;
                                    path.style.strokeDashoffset = length;
                                }
                            });

                            tl.to(paths, {
                                strokeDashoffset: 0,
                                duration: 1.5,
                                ease: 'power2.inOut',
                                stagger: 0.1
                            }, 0.2);
                        }
                    }
                } else {
                    // Image fallback: scale and fade effect
                    console.log('[BLOCK] Not SVG, using scale animation');
                    gsap.set(hallmarkLogo, { scale: 0.8 });

                    tl.to(hallmarkWrapper, {
                        opacity: 1,
                        force3D: false,
                        duration: 0.5
                    }, '-=0.3')
                    .to(hallmarkLogo, {
                        scale: 1,
                        duration: 0.5,
                        ease: 'back.out(1.7)'
                    }, '-=0.3');
                }
            }

            // Animate other content elements with stagger
            const contentElements = block.querySelectorAll('[data-gsap-animate]');
            if (contentElements.length > 0) {
                gsap.set(contentElements, { opacity: 0, y: 20 });
                tl.to(contentElements, {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    stagger: 0.1,
                    ease: 'power2.out'
                }, '-=0.5');
            }

            console.log('[BLOCK] Header animations complete');
        }

        // Wait for GSAP to be available
        if (typeof gsap !== 'undefined') {
            runAnimations();
        } else {
            // Poll for GSAP with timeout
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max
            const checkGSAP = setInterval(() => {
                attempts++;
                if (typeof gsap !== 'undefined') {
                    clearInterval(checkGSAP);
                    runAnimations();
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkGSAP);
                    console.log('[BLOCK] GSAP not available after timeout, skipping animations');
                }
            }, 100);
        }
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initHeaderFullScreen);
    } else {
        initHeaderFullScreen();
    }
})();